import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { useAppShell } from "@sylviq/ui-react-web/components/shell/AppShell";
import {
  SidebarNavProvider,
  useSidebarNavState,
} from "@sylviq/ui-react-web/components/shell/SidebarNav";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Brand lockup row. Products supply their own mark and wordmark. */
  lockup?: ReactNode;
  /** Optional row under the lockup — a workspace or tenant switcher. */
  aside?: ReactNode;
  /** Bottom row: identity and quick actions. */
  footer?: ReactNode;
  expandLabel?: string;
  collapseLabel?: string;
  /** Navigation. Put `<NavSection>`s in it for one icon per section. */
  children: ReactNode;
}

/**
 * Floating sidebar card. Always an overlay — see `<AppShell>`.
 *
 * Three regions: a locked header (lockup + `aside`), the scrolling middle
 * (`children`), and a locked footer. Choosing a `<NavSection>` opens its
 * views in a flyout flush with the card, collapsed or expanded.
 *
 * The collapse control travels: expanded it straddles the card's edge,
 * collapsed it slides to the rail's centre and takes the lockup's header
 * position. The section flyout stays below that control.
 */
export function Sidebar({
  lockup,
  aside,
  footer,
  expandLabel = "Expand navigation",
  collapseLabel = "Collapse navigation",
  className,
  children,
  ...props
}: SidebarProps) {
  const { collapsed, toggle } = useAppShell();
  const toggleLabel = collapsed ? expandLabel : collapseLabel;
  const nav = useSidebarNavState(collapsed);

  return (
    <SidebarNavProvider value={nav}>
      <aside className={cn("app-sidebar", className)} {...props}>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="app-sidebar-toggle"
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          title={toggleLabel}
          onClick={toggle}
        >
          {collapsed ? (
            <PanelLeftOpen className="size-4" />
          ) : (
            <PanelLeftClose className="size-4" />
          )}
        </Button>
        {/* Locked: never scrolls, whatever the navigation grows to. */}
        <div className="app-sidebar-top">
          {!collapsed && lockup ? (
            <div className="app-sidebar-header">{lockup}</div>
          ) : null}
          {collapsed ? <div className="app-sidebar-rail-header" /> : null}
          {aside ? <div className="app-sidebar-aside">{aside}</div> : null}
        </div>
        <div className="app-sidebar-scroll">{children}</div>
        {footer ? <div className="nav-footer">{footer}</div> : null}
      </aside>
    </SidebarNavProvider>
  );
}
